'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { filterPrivateInfo, containsPrivateInfo } from '@/lib/privacyFilter';
import { Send, ShieldCheck, Lock, AlertTriangle, ArrowLeft, CheckCheck, Check, CalendarDays, CheckCircle2, ChevronDown,  } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

// ── Types ────────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  read_at: string | null;
  message_type: 'text' | 'booking_update' | 'system';
  created_at: string;
}

interface Conversation {
  id: string;
  employer_id: string;
  worker_id: string;
  booking_ref: string | null;
  booking_status: string | null;
  status: string;
  updated_at: string;
  other_party_name: string;
  other_party_avatar: string | null;
  other_party_role: string;
  unread_count: number;
  last_message: string | null;
}

interface ChatInterfaceProps {
  role: 'employer' | 'worker';
}

// ── Booking status config ────────────────────────────────────────────────────

const BOOKING_STATUSES = [
  { value: 'requested',  label: 'Requested',  bg: '#EEF2FF', color: '#4F46E5' },
  { value: 'accepted',   label: 'Accepted',   bg: '#F0FDF4', color: '#16A34A' },
  { value: 'confirmed',  label: 'Confirmed',  bg: 'var(--orange-pale)', color: 'var(--primary)' },
  { value: 'in_progress',label: 'In Progress',bg: '#FEF9C3', color: '#854D0E' },
  { value: 'completed',  label: 'Completed',  bg: '#F0FDF4', color: '#15803D' },
  { value: 'cancelled',  label: 'Cancelled',  bg: '#FEF2F2', color: '#DC2626' },
  { value: 'declined',   label: 'Declined',   bg: '#FEF2F2', color: '#DC2626' },
];

function getBookingStatus(value: string | null) {
  return BOOKING_STATUSES.find((s) => s.value === value) ?? null;
}

// ── Main component ───────────────────────────────────────────────────────────

export default function ChatInterface({ role }: ChatInterfaceProps) {
  const { user } = useAuth();
  const supabase = createClient();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [privacyWarning, setPrivacyWarning] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);

  const activeConv = conversations.find((c) => c.id === activeConvId) ?? null;

  // ── Close status menu on outside click ──────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load conversations ───────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!user) return;
    setLoadingConvs(true);
    try {
      const { data: convData, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`employer_id.eq.${user.id},worker_id.eq.${user.id}`)
        .order('updated_at', { ascending: false });

      if (error) { console.error(error); return; }

      const enriched: Conversation[] = await Promise.all(
        (convData ?? []).map(async (conv: any) => {
          const otherId = role === 'employer' ? conv.worker_id : conv.employer_id;

          const { data: profile } = await supabase
            .from('user_profiles')
            .select('full_name, avatar_url, role')
            .eq('id', otherId)
            .maybeSingle();

          const { data: lastMsg } = await supabase
            .from('messages')
            .select('content, message_type')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const { count: unread } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('is_read', false)
            .neq('sender_id', user.id);

          const lastMsgText = lastMsg?.message_type === 'booking_update' ?'📋 Booking status updated'
            : lastMsg?.content ?? null;

          return {
            ...conv,
            other_party_name: profile?.full_name ?? (role === 'employer' ? 'Candidate' : 'Employer'),
            other_party_avatar: profile?.avatar_url ?? null,
            other_party_role: profile?.role ?? (role === 'employer' ? 'worker' : 'employer'),
            unread_count: unread ?? 0,
            last_message: lastMsgText,
          };
        })
      );

      setConversations(enriched);
    } finally {
      setLoadingConvs(false);
    }
  }, [user, role, supabase]);

  // ── Load messages ────────────────────────────────────────────────────────────
  const loadMessages = useCallback(async (convId: string) => {
    setLoadingMsgs(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });

      if (error) { console.error(error); return; }
      setMessages(data ?? []);

      // Mark unread messages as read
      if (user) {
        const now = new Date().toISOString();
        await supabase
          .from('messages')
          .update({ is_read: true, read_at: now })
          .eq('conversation_id', convId)
          .eq('is_read', false)
          .neq('sender_id', user.id);
      }
    } finally {
      setLoadingMsgs(false);
    }
  }, [supabase, user]);

  // ── Initial load ─────────────────────────────────────────────────────────────
  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Load messages when active conv changes ───────────────────────────────────
  useEffect(() => {
    if (activeConvId) loadMessages(activeConvId);
  }, [activeConvId, loadMessages]);

  // ── Scroll to bottom ─────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Real-time: messages subscription ────────────────────────────────────────
  useEffect(() => {
    if (!activeConvId || !user) return;

    const channel = supabase
      .channel(`messages:${activeConvId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${activeConvId}` },
        async (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          // Auto-mark as read if from other party
          if (newMsg.sender_id !== user.id) {
            const now = new Date().toISOString();
            await supabase
              .from('messages')
              .update({ is_read: true, read_at: now })
              .eq('id', newMsg.id);
          }
          loadConversations();
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages', filter: `conversation_id=eq.${activeConvId}` },
        (payload) => {
          const updated = payload.new as Message;
          setMessages((prev) => prev.map((m) => m.id === updated.id ? updated : m));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [activeConvId, user, supabase, loadConversations]);

  // ── Real-time: conversation list subscription ────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`conversations:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'conversations' },
        () => { loadConversations(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, supabase, loadConversations]);

  // ── Send message ─────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!draft.trim() || !activeConvId || !user || sending) return;
    const filtered = filterPrivateInfo(draft.trim());
    setSending(true);
    setDraft('');
    setPrivacyWarning(false);
    const { error } = await supabase.from('messages').insert({
      conversation_id: activeConvId,
      sender_id: user.id,
      content: filtered,
      message_type: 'text',
    });
    if (error) console.error(error);
    setSending(false);
    inputRef.current?.focus();
  };

  // ── Update booking status ────────────────────────────────────────────────────
  const handleBookingStatusUpdate = async (newStatus: string) => {
    if (!activeConvId || !user || updatingStatus) return;
    setUpdatingStatus(true);
    setShowStatusMenu(false);

    const statusLabel = BOOKING_STATUSES.find((s) => s.value === newStatus)?.label ?? newStatus;

    // Update conversation booking_status
    const { error: convErr } = await supabase
      .from('conversations')
      .update({ booking_status: newStatus })
      .eq('id', activeConvId);

    if (convErr) { console.error(convErr); setUpdatingStatus(false); return; }

    // Insert a booking_update system message
    const { error: msgErr } = await supabase.from('messages').insert({
      conversation_id: activeConvId,
      sender_id: user.id,
      content: `Booking status updated to: ${statusLabel}`,
      message_type: 'booking_update',
    });

    if (msgErr) console.error(msgErr);

    // Refresh conversations to reflect new status
    await loadConversations();
    setUpdatingStatus(false);
  };

  const handleDraftChange = (val: string) => {
    setDraft(val);
    setPrivacyWarning(containsPrivateInfo(val));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const selectConversation = (id: string) => {
    setActiveConvId(id);
    setMobileShowChat(true);
    setShowStatusMenu(false);
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  };

  // ── Read receipt icon ────────────────────────────────────────────────────────
  const ReadReceipt = ({ msg }: { msg: Message }) => {
    if (msg.sender_id !== user?.id) return null;
    if (msg.is_read) {
      return <CheckCheck size={13} style={{ color: '#60A5FA' }} className="inline ml-1 flex-shrink-0" />;
    }
    return <Check size={13} style={{ color: 'rgba(255,255,255,0.6)' }} className="inline ml-1 flex-shrink-0" />;
  };

  // ── Booking update bubble ────────────────────────────────────────────────────
  const BookingUpdateBubble = ({ msg }: { msg: Message }) => {
    const statusMatch = msg.content.match(/Booking status updated to: (.+)/);
    const statusLabel = statusMatch?.[1] ?? 'Updated';
    const statusEntry = BOOKING_STATUSES.find((s) => s.label === statusLabel);
    return (
      <div className="flex justify-center my-2">
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border"
          style={{
            background: statusEntry?.bg ?? '#F3F4F6',
            color: statusEntry?.color ?? '#374151',
            borderColor: statusEntry?.color ? `${statusEntry.color}30` : '#E5E7EB',
          }}
        >
          <CalendarDays size={12} />
          <span>Booking status → <strong>{statusLabel}</strong></span>
          <span style={{ opacity: 0.6 }}>{formatTime(msg.created_at)}</span>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full rounded-2xl overflow-hidden border border-border bg-white" style={{ minHeight: '600px' }}>

      {/* ── Conversation list ── */}
      <div
        className={`flex flex-col border-r border-border bg-white flex-shrink-0 ${
          mobileShowChat ? 'hidden md:flex' : 'flex'
        } w-full md:w-72 lg:w-80`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-extrabold text-lg" style={{ color: 'var(--navy)' }}>Messages</h2>
            <Lock size={14} style={{ color: 'var(--primary)' }} />
          </div>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            All chats are private. Contact info is never shared.
          </p>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {loadingConvs ? (
            <div className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-2" style={{ borderColor: 'var(--primary)' }} />
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Loading…</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <Lock size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--navy)' }}>No conversations yet</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {role === 'employer' ? 'Start a chat from a candidate profile.' : 'Employers will message you here.'}
              </p>
            </div>
          ) : (
            conversations.map((conv) => {
              const bs = getBookingStatus(conv.booking_status);
              return (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv.id)}
                  className={`w-full flex items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/30 ${
                    activeConvId === conv.id ? 'bg-orange-50' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    {conv.other_party_avatar ? (
                      <AppImage
                        src={conv.other_party_avatar}
                        alt={conv.other_party_name}
                        width={42}
                        height={42}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: 'var(--primary)' }}>
                        {conv.other_party_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: 'var(--vetted-color)' }}>
                      <ShieldCheck size={8} color="white" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-bold truncate" style={{ color: 'var(--navy)' }}>
                        {conv.other_party_name}
                      </span>
                      <span className="text-xs flex-shrink-0 ml-2" style={{ color: 'var(--muted-foreground)' }}>
                        {formatTime(conv.updated_at)}
                      </span>
                    </div>
                    {bs && (
                      <span
                        className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-semibold mb-0.5"
                        style={{ background: bs.bg, color: bs.color }}
                      >
                        <CalendarDays size={9} />
                        {bs.label}
                      </span>
                    )}
                    <p
                      className="text-xs truncate"
                      style={{
                        color: conv.unread_count > 0 ? 'var(--foreground)' : 'var(--muted-foreground)',
                        fontWeight: conv.unread_count > 0 ? '600' : '400',
                      }}
                    >
                      {conv.last_message ?? 'No messages yet'}
                    </p>
                  </div>

                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 rounded-full text-xs font-bold text-white flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--primary)' }}>
                      {conv.unread_count}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── Chat panel ── */}
      <div className={`flex-1 flex flex-col ${!mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
        {activeConv ? (
          <>
            {/* Chat header */}
            <div className="px-5 py-3 border-b border-border flex items-center gap-3 bg-white">
              <button
                className="md:hidden p-1.5 rounded-lg hover:bg-muted mr-1"
                onClick={() => setMobileShowChat(false)}
              >
                <ArrowLeft size={18} style={{ color: 'var(--navy)' }} />
              </button>

              {activeConv.other_party_avatar ? (
                <AppImage
                  src={activeConv.other_party_avatar}
                  alt={activeConv.other_party_name}
                  width={38}
                  height={38}
                  className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: 'var(--primary)' }}>
                  {activeConv.other_party_name.charAt(0).toUpperCase()}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate" style={{ color: 'var(--navy)' }}>
                  {activeConv.other_party_name}
                </p>
                <p className="text-xs capitalize" style={{ color: 'var(--muted-foreground)' }}>
                  {activeConv.other_party_role === 'worker' ? 'Candidate' : 'Employer'}
                  {activeConv.booking_ref ? ` · Booking #${activeConv.booking_ref}` : ''}
                </p>
              </div>

              {/* Booking status pill + quick update */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {activeConv.booking_status && (() => {
                  const bs = getBookingStatus(activeConv.booking_status);
                  return bs ? (
                    <span
                      className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
                      style={{ background: bs.bg, color: bs.color }}
                    >
                      <CalendarDays size={11} />
                      {bs.label}
                    </span>
                  ) : null;
                })()}

                {/* Quick status update dropdown */}
                <div className="relative" ref={statusMenuRef}>
                  <button
                    onClick={() => setShowStatusMenu((v) => !v)}
                    disabled={updatingStatus}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 active:scale-95 disabled:opacity-50"
                    style={{ background: 'var(--orange-pale)', color: 'var(--primary)' }}
                    title="Update booking status"
                  >
                    <CalendarDays size={13} />
                    <span className="hidden sm:inline">Status</span>
                    <ChevronDown size={12} />
                  </button>

                  {showStatusMenu && (
                    <div
                      className="absolute right-0 top-full mt-1.5 w-44 rounded-xl border border-border bg-white shadow-lg z-50 overflow-hidden"
                    >
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-xs font-bold" style={{ color: 'var(--navy)' }}>Update Booking Status</p>
                      </div>
                      {BOOKING_STATUSES.map((s) => (
                        <button
                          key={s.value}
                          onClick={() => handleBookingStatusUpdate(s.value)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-left hover:bg-muted/40 transition-colors"
                          style={{ color: s.color }}
                        >
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                          {s.label}
                          {activeConv.booking_status === s.value && (
                            <CheckCircle2 size={12} className="ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--orange-pale)', color: 'var(--primary)' }}>
                  <Lock size={11} />
                  <span className="hidden sm:inline">Private</span>
                </div>
              </div>
            </div>

            {/* Privacy notice */}
            <div className="px-5 py-2 flex items-center gap-2 text-xs" style={{ background: '#F0FDF4', color: '#15803D', borderBottom: '1px solid #BBF7D0' }}>
              <ShieldCheck size={13} />
              <span>Phone numbers, emails, and contact details are automatically removed to protect your privacy.</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
              {loadingMsgs ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)' }} />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                  <Lock size={36} className="mb-3" style={{ color: 'var(--border)' }} />
                  <p className="text-sm font-semibold mb-1" style={{ color: 'var(--navy)' }}>Start the conversation</p>
                  <p className="text-xs max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
                    Introduce yourself and discuss the care needs. All messages are private and secure.
                  </p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  if (msg.message_type === 'booking_update') {
                    return <BookingUpdateBubble key={msg.id} msg={msg} />;
                  }

                  const isMine = msg.sender_id === user?.id;
                  const prevMsg = idx > 0 ? messages[idx - 1] : null;
                  const showDateSep = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();

                  return (
                    <React.Fragment key={msg.id}>
                      {showDateSep && (
                        <div className="flex justify-center my-3">
                          <span className="text-xs px-3 py-1 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                            {new Date(msg.created_at).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1`}>
                        <div
                          className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                            isMine ? 'rounded-br-sm' : 'rounded-bl-sm'
                          }`}
                          style={{
                            background: isMine ? 'var(--primary)' : 'var(--muted)',
                            color: isMine ? '#fff' : 'var(--foreground)',
                          }}
                        >
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-xs" style={{ opacity: 0.65 }}>
                              {formatTime(msg.created_at)}
                            </span>
                            <ReadReceipt msg={msg} />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="px-4 py-3 border-t border-border bg-white">
              {privacyWarning && (
                <div className="flex items-center gap-2 text-xs mb-2 px-3 py-2 rounded-lg" style={{ background: '#FEF9C3', color: '#854D0E' }}>
                  <AlertTriangle size={13} />
                  <span>Contact info detected — it will be removed automatically before sending.</span>
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => handleDraftChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message… (Enter to send)"
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{
                    maxHeight: '120px',
                    overflowY: 'auto',
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!draft.trim() || sending}
                  className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-150 active:scale-95 disabled:opacity-40"
                  style={{ background: 'var(--primary)' }}
                >
                  <Send size={16} color="white" />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--orange-pale)' }}>
              <Lock size={28} style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--navy)' }}>Privacy-first messaging</h3>
            <p className="text-sm max-w-sm" style={{ color: 'var(--muted-foreground)' }}>
              Select a conversation to start chatting. Phone numbers, emails, and contact details are never shared.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
